import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Row, Typography } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { PixelCrop, Crop } from 'react-image-crop';

import { omit, pick } from 'utils/helpers';
import { useDeepEffect } from 'utils/useDeepEffect';
import yup from 'utils/yup';
import { getImageUrl } from 'utils/image';
import { TImage } from 'types/image';

import { CheckboxField } from '../base/CheckboxField';
import { InputField } from '../base/InputField';
import { TextAreaField } from '../base/TextAreaField';
import { FloatButtons } from '../FloatButtons/FloatButtons';
import { MyCropper } from '../../Cropper/cropper';
import { imgPreview } from '../../Cropper/imgPreview';
import { canvasPreview } from '../../Cropper/canvasPreview';
import styles from './CropperModal.module.less';
import {
  centerAspectCrop,
  leftAspectCrop,
  rightAspectCrop,
} from '../../Cropper/centerAcpectCrop';

type TRequestData = {
  crop: any;
  cropCanvas: any;
  source: string;
  author: string;
  description?: string;
};

type TProps = {
  value: TImage;
  onChange: (value: any) => void;
  setCroppedImageUrl?: (value: string) => void;
  originalImageUrl: string;
  setCropModalIsOpen: (value: boolean) => void;
  cropModalIsOpen: boolean;
  aspectRatio?: number;
  watchAspectRatio?: boolean;
  isNoInputs?: boolean;
  setModalIsOpen?: (value: boolean) => void;
  isShowPositionFields?: boolean;
};

export const validationSchema = yup.object().shape({
  source: yup.string(),
  author: yup.string().required(),
  description: yup.string(),
});

const emptySchema = yup.object().shape({});

export const CropperModal: React.FC<TProps> = ({
  value,
  onChange,
  setCroppedImageUrl,
  originalImageUrl,
  setCropModalIsOpen,
  cropModalIsOpen,
  aspectRatio = 3 / 2,
  watchAspectRatio,
  isNoInputs,
  setModalIsOpen,
  isShowPositionFields = false,
}) => {
  const resolver = yupResolver(isNoInputs ? emptySchema : validationSchema);
  const [crop, setCrop] = useState<Crop>();
  const [completeCrop, setCompleteCrop] = React.useState<PixelCrop>();
  const [aspect, setAspect] = React.useState(aspectRatio);
  const imgRef = useRef<HTMLImageElement>(null);
  const initialData = value?.crop || undefined;
  const methods = useForm<TImage>({
    defaultValues: value,
    resolver,
  });

  const handleChangeComplete = (crop: PixelCrop) => {
    setCompleteCrop(crop);
  };

  const isFullImage = methods.watch('isFull');

  const submit = async ({
    description,
    author,
    source,
    isFull,
    float,
  }: TImage) => {
    const cropper = completeCrop;
    console.log('COMPLETE_CROPPER', cropper);
    console.log('INITIAL_CRopper', initialData);
    const data = pick(cropper, 'x', 'y', 'width', 'height');
    const canvas = document.createElement('canvas');
    await canvasPreview(imgRef.current, canvas, cropper, 1, 0);
    const position = isShowPositionFields
      ? {
          isFull,
          float,
        }
      : {};
    const requestData: TRequestData = {
      ...omit(value, 'description'),
      crop: data,
      cropCanvas: canvas,
      source: source || ' ',
      author,
      ...position,
    };

    if (description?.length) {
      requestData.description = description;
    }
    onChange(requestData);
    if (typeof setCroppedImageUrl === 'function') {
      if ('path' in value) {
        setCroppedImageUrl(getImageUrl(requestData));
      } else {
        const url = await imgPreview(canvas);
        setCroppedImageUrl(url);
      }
    }
    setCropModalIsOpen(false);
    if (setModalIsOpen) setModalIsOpen(false);
  };

  const handleCancel = () => {
    setCropModalIsOpen(false);
    if (setModalIsOpen) setModalIsOpen(false);
  };

  const handleFullChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    const { width, height } = imgRef.current;
    methods.setValue('isFull', e.target.checked);
    if (checked) {
      methods.setValue('float', '');
      setAspect(aspectRatio);
      setCrop(centerAspectCrop(width, height, aspectRatio, 100));
    }
  };

  const handleChangeFloat = e => {
    const value = e.target.value;
    const { width, height } = imgRef.current;
    if (value === 'left') {
      setAspect(3 / 4);
      setCrop(leftAspectCrop(width, height, 3 / 4));
      return;
    }

    if (value === 'right') {
      setAspect(3 / 4);
      setCrop(rightAspectCrop(width, height, 3 / 4));
      return;
    }
    setAspect(aspectRatio);
    setCrop(centerAspectCrop(width, height, aspectRatio));
  };

  useEffect(() => {
    if (watchAspectRatio && imgRef.current) {
      const { width, height } = imgRef.current;
      setAspect(aspectRatio);
      setCrop(centerAspectCrop(width, height, aspectRatio))
    }
  }, [aspectRatio, watchAspectRatio, imgRef.current]);

  useDeepEffect(() => {
    const position = isShowPositionFields
      ? { isFull: value?.isFull, float: value?.float }
      : {};

    methods.reset({
      author: value?.author,
      description: value?.description,
      source: value?.source,
      ...position,
    });
  }, [value]);

  return (
    <Modal
      title='Редактировать изображение'
      open={cropModalIsOpen}
      onCancel={handleCancel}
      width={840}
      footer={[
        <Button key='cancel' onClick={handleCancel}>
          Отмена
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={() => methods.handleSubmit(submit)()}
        >
          Применить
        </Button>,
      ]}
      destroyOnClose
    >
      <FormProvider {...methods}>
        <form
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className='ant-form ant-form-vertical'
        >
          <Row>
            {!isNoInputs && (
              <div className={styles.inputs}>
                <InputField
                  required
                  label='Автор'
                  name='author'
                  placeholder='Введите имя автора'
                  autoFocus
                  showCount
                  maxLength={255}
                />
                <InputField
                  label='Ссылка на источник'
                  name='source'
                  placeholder='Укажите ссылку на источник'
                  showCount
                  maxLength={255}
                />
                {isShowPositionFields && (
                  <>
                    <Form.Item
                      className={styles.imageSettimg}
                      label='Положение изображения'
                    >
                      <FloatButtons
                        handleChange={handleChangeFloat}
                        name='float'
                        disabledButton={isFullImage}
                      />
                      <CheckboxField
                        formItemStyle={{ marginBottom: 0 }}
                        name='isFull'
                        onChange={handleFullChange}
                      >
                        На всю ширину
                      </CheckboxField>
                    </Form.Item>
                  </>
                )}

                <TextAreaField
                  label='Описание изображения'
                  name='description'
                  placeholder='Описание изображения'
                  showCount
                  maxLength={350}
                  rows={10}
                />
              </div>
            )}

            <div className={styles.image}>
              <Typography.Text>Редактировать фотографию</Typography.Text>

              <div className={styles.image__in}>
                <div className={styles.image__wrapper}>
                  <MyCropper
                    initialCrop={initialData as Crop}
                    crop={crop}
                    setCrop={setCrop}
                    imgSrc={originalImageUrl}
                    handleCompleteCrop={handleChangeComplete}
                    aspect={aspect}
                    imgRef={imgRef}
                  />
                </div>
              </div>
            </div>
          </Row>
        </form>
      </FormProvider>
    </Modal>
  );
};
